import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function TeacherGameControl({ user }) {
  const navigate = useNavigate();
  const { pin } = useParams();
  const [socket, setSocket] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);

  useEffect(() => {
    const newSocket = io(BACKEND_URL, {
      transports: ['polling', 'websocket'],
      upgrade: true,
    });

    newSocket.on('connect', () => {
      console.log('Teacher control connected');
      newSocket.emit('join_teacher', { pin });
    });

    newSocket.on('new_question', (data) => {
      setCurrentQuestion(data.question_number);
      setTotalQuestions(data.total_questions);
    });

    newSocket.on('game_paused', () => {
      setIsPaused(true);
    });

    newSocket.on('game_resumed', () => {
      setIsPaused(false);
    });

    newSocket.on('game_finished', () => {
      navigate('/dashboard');
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [pin, navigate]);

  const handlePauseGame = () => {
    if (socket) {
      socket.emit('pause_game', { pin });
    }
  };

  const handleResumeGame = () => {
    if (socket) {
      socket.emit('resume_game', { pin });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary to-purple-900 text-white" data-testid="teacher-game-control">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-2">Juego en Progreso</h1>
          <p className="text-xl text-purple-200">PIN: {pin}</p>
          {totalQuestions > 0 && (
            <p className="text-lg text-purple-300 mt-2">
              Pregunta {currentQuestion} de {totalQuestions}
            </p>
          )}
        </div>

        <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-8 mb-6">
          <h2 className="text-2xl font-bold mb-6 text-center">Controles del Juego</h2>
          
          <div className="flex flex-col gap-4">
            {isPaused ? (
              <button
                onClick={handleResumeGame}
                className="w-full px-8 py-6 bg-green-600 text-white text-xl font-bold rounded-xl hover:bg-green-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
              >
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                ▶️ Reanudar Juego
              </button>
            ) : (
              <button
                onClick={handlePauseGame}
                className="w-full px-8 py-6 bg-yellow-600 text-white text-xl font-bold rounded-xl hover:bg-yellow-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-3"
              >
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
                ⏸️ Pausar Juego
              </button>
            )}

            <div className="text-center text-sm text-purple-200">
              {isPaused 
                ? "⏸️ El juego está pausado. Los estudiantes no pueden responder."
                : "✓ El juego está en curso."
              }
            </div>
          </div>
        </div>

        <div className="bg-white bg-opacity-10 backdrop-blur-lg rounded-2xl p-6">
          <h3 className="text-xl font-bold mb-4">📊 Información</h3>
          <ul className="space-y-2 text-purple-100">
            <li>• Los estudiantes están respondiendo preguntas</li>
            <li>• El juego avanza automáticamente después de cada pregunta</li>
            <li>• Usa el botón de pausa si necesitas hacer una aclaración</li>
          </ul>
        </div>

        <button
          onClick={() => navigate('/dashboard')}
          className="w-full mt-6 px-6 py-3 bg-white bg-opacity-20 text-white font-semibold rounded-xl hover:bg-opacity-30 transition"
        >
          ← Volver al Dashboard
        </button>
      </div>
    </div>
  );
}

export default TeacherGameControl;
